import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass, Transition as _Transition, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "popup__header"
}
const _hoisted_2 = ["innerHTML"]
const _hoisted_3 = { class: "popup__inner" }
const _hoisted_4 = {
  key: 0,
  class: "popup__buttons"
}

import { closestMatching } from '@/helpers/dom'
import { platform } from '@/services/platform'
import { computed, nextTick, onUnmounted, ref, useTemplateRef } from 'vue'

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'

// In activeElementBeforeShow, store the element that had focus before opening
// the menu, so we can move focus back to that once the menu is closed.
const ANCHOR_GAP = 8
// The minimum distance between the popup and the viewport boundary.
const VIEWPORT_MARGIN = 16

export default /*@__PURE__*/_defineComponent({
  __name: 'BasePopup',
  props: {
    title: {},
    compact: { type: Boolean },
    fullscreen: { type: Boolean },
    popout: { type: Boolean },
    menu: { type: Boolean },
    anchor: {}
  },
  emits: ["hide"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props

const emit = __emit

const popupElement = useTemplateRef('popupElement')
const isVisible = ref(false)

// Selector for any focusable element. Use this when moving focus to the menu.
let activeElementBeforeShow: HTMLElement | null = null

function hide(trackEvent: boolean = true): void {
  const wasVisible = isVisible.value
  isVisible.value = false
  document.removeEventListener('keydown', onKeydown)
  window.removeEventListener('resize', updateAnchorPosition)
  if (props.menu && wasVisible) {
    activeElementBeforeShow?.focus({ preventScroll: true })
    activeElementBeforeShow = null
  }
  emit('hide', trackEvent)
}

function show(): void {
  isVisible.value = true
  currentY.value = 0
  document.addEventListener('keydown', onKeydown)
  if (props.menu) {
    // In menu mode, move focus to itself when it opens, so that keyboard users
    // don't have to tab through the page behind it to reach it.
    activeElementBeforeShow =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null
    // Wait for the menu to render before focusing its first item.
    nextTick(() => {
      popupElement.value
        ?.querySelector<HTMLElement>(FOCUSABLE)
        ?.focus({ preventScroll: true })
    })
  }
  if (props.anchor) {
    // Follow the anchor if the window is resized (e.g. the page reflows).
    // There's no scroll listener: the backdrop blocks wheel scrolling, and
    // keyboard scrolling has no effect because focus isn't inside the page's
    // scroller (`#app`). The one exception is tabbing to an off-screen
    // element behind the popup, which we don't handle.
    window.addEventListener('resize', updateAnchorPosition)
    // Wait for the popup to render so that we know its size to position it.
    nextTick(updateAnchorPosition)
  }
}

// The space between the anchor element and the popup.
const anchorPosition = ref<Record<string, string>>({})

/**
 * On desktop, position the popup next to the anchor element.
 */
function updateAnchorPosition(): void {
  // The popup isn't rendered if it was hidden before the `nextTick()` in
  // `show()` ran.
  const popup = popupElement.value
  if (!props.anchor || !popup) {
    return
  }

  const anchorRect = props.anchor.getBoundingClientRect()

  // Vertically clamp to the viewport.
  const topIfBelow = Math.max(VIEWPORT_MARGIN, anchorRect.bottom + ANCHOR_GAP)
  const bottomIfAbove = Math.max(
    VIEWPORT_MARGIN,
    window.innerHeight - anchorRect.top + ANCHOR_GAP,
  )
  const spaceBelow = window.innerHeight - topIfBelow - VIEWPORT_MARGIN
  const spaceAbove = window.innerHeight - bottomIfAbove - VIEWPORT_MARGIN

  // Calculate the popup's full height, including any content cut off by max-height.
  // - scrollHeight: content + padding + content hidden by max-height
  // - offsetHeight: content + padding + border
  // - clientHeight: content + padding
  const height = popup.scrollHeight + popup.offsetHeight - popup.clientHeight

  // Position the popup
  // - below the anchor element if it fits (spaceBelow >= height)
  // - otherwise above if it fits there
  // - otherwise whichever side has more room
  // The second condition in the OR checks if the popup doesn't fit above
  // (spaceAbove < height) and if there is less space above than below
  // (spaceBelow >= spaceAbove).
  const placePopupBelowAnchor =
    spaceBelow >= height || (spaceAbove < height && spaceBelow >= spaceAbove)

  // `right` is the distance from the right edge of the window to the right
  // edge of the popup.
  // Right-align to the anchor element when possible, while keeping the popup
  // inside the viewport.
  const right = Math.min(
    Math.max(
      // The "normal" case: just the right edge of the anchor element.
      window.innerWidth - anchorRect.right,
      // The right edge of the anchor element is too close to the viewport
      // right edge. Use the minimum margin from the viewport.
      VIEWPORT_MARGIN,
    ),
    // The left edge of the popup is too close to the viewport left edge.
    // Subtract (popup width + minimum margin) from the window's width to get
    // the leftmost position we allow for the right edge of the popup.
    window.innerWidth - popup.offsetWidth - VIEWPORT_MARGIN,
  )

  anchorPosition.value = {
    '--popup-anchor-top': placePopupBelowAnchor ? `${topIfBelow}px` : 'auto',
    '--popup-anchor-bottom': placePopupBelowAnchor
      ? 'auto'
      : `${bottomIfAbove}px`,
    '--popup-anchor-right': `${right}px`,
    '--popup-anchor-max-height': `${placePopupBelowAnchor ? spaceBelow : spaceAbove}px`,
  }
}

/**
 * Hide the popup when the user presses "Esc".
 */
function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    hide()
  }
}

onUnmounted(() => {
  document.removeEventListener('keydown', onKeydown)
  window.removeEventListener('resize', updateAnchorPosition)
})

// The logic below handles the logic for closing the popup
// by swiping down. It's used only at the mobile breakpoint,
// where the popup is styled as a bottom sheet element.
const sheetHeight = ref(0)
const startTime = ref(0)
const startY = ref(0)
const currentY = ref(0)
const currentPositionCssVariable = computed(() => {
  return `--sheet-current-position: ${currentY.value}px`
})

function onTouchStart(event: TouchEvent): void {
  if (props.fullscreen || !platform.isMediumWidth()) {
    // Disable drag-to-close if the popup is set to fullscreen,
    // or if it's above the medium breakpoint (tablet and desktop).
    return
  }

  if (isScrolling(event.target as HTMLElement)) {
    // Set `startTime` to 0 to indicate that we shouldn't
    // run the remaining event handlers (otherwise scrolling
    // content would drag the whole sheet with it).
    startTime.value = 0
    return
  }

  // This will be used to calculate the swipe velocity
  startTime.value = Date.now()
  // This will be used to calculate the swipe distance.
  startY.value = event.touches[0].clientY
  // Update sheetHeight if the element's height has changed.
  sheetHeight.value = popupElement.value?.offsetHeight || sheetHeight.value
  // Mark the element as being dragged. This is used in the CSS
  // to remove the `transition` property (the element's position
  // should update immediately, otherwise it feels unresponsive).
  if (popupElement.value) {
    popupElement.value.dataset.isDragged = 'true'
  }
}

function onTouchMove(event: TouchEvent): void {
  if (!startTime.value) {
    // No `startTime` indicates that we shouldn't run update the sheet's positon
    // because the user is currently scrolling the content inside.
    return
  }

  // The current position of the finger
  const touchY = event.touches[0].clientY
  const distanceMoved = touchY - startY.value

  if (distanceMoved <= 0) {
    // Don't let the component move beyond its initial position
    currentY.value = 0
  } else {
    currentY.value = distanceMoved
  }
}

function onTouchEnd(): void {
  // End the `isDragged` state.
  delete popupElement.value?.dataset.isDragged

  if (!startTime.value) {
    // Reset the sheet's position, just in case.
    currentY.value = 0
    return
  }

  // The time spent dragging
  const timeSpan = Date.now() - startTime.value
  // The velocity the drag action
  const velocity = currentY.value / timeSpan

  // Threshold velocity in pixels per millisecond.
  // There's not much science to this number, it's just
  // a value that feels good.
  const velocityThreshold = 0.2
  const halfComponentHeight = sheetHeight.value / 2

  if (
    (velocity > velocityThreshold && currentY.value > 50) ||
    currentY.value > halfComponentHeight
  ) {
    // If the user swiped quick enough or if they
    // swiped to below half the height of the element,
    // close it.
    hide()
  } else {
    // Spring it back to its initial position
    currentY.value = 0
  }

  // Reset the startTime.
  startTime.value = 0
}

/**
 * Check if the touch event target is being scrolled.
 */
function isScrolling(targetElement: HTMLElement): boolean | null {
  const scrollingElement = closestMatching(
    targetElement,
    (element) => element.scrollHeight > element.clientHeight,
    popupElement.value,
    true,
  )
  return scrollingElement && scrollingElement.scrollTop > 0
}

__expose({
  show,
  hide,
  isVisible,
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Transition, { name: "popup-transition" }, {
    default: _withCtx(() => [
      (isVisible.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: _normalizeClass(["popup", {
        'popup--compact': __props.compact,
        'popup--fullscreen': __props.fullscreen,
        'popup--popout': __props.popout,
        'popup--menu': __props.menu,
        'popup--anchored': !!__props.anchor,
      }]),
            onClick: _cache[2] || (_cache[2] = ($event: any) => ($event.stopPropagation()))
          }, [
            _createElementVNode("div", {
              class: "popup__background",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (hide()))
            }),
            _createElementVNode("div", {
              ref_key: "popupElement",
              ref: popupElement,
              class: "popup__window",
              style: _normalizeStyle([currentPositionCssVariable.value, anchorPosition.value]),
              onTouchstart: onTouchStart,
              onTouchmove: onTouchMove,
              onTouchend: onTouchEnd,
              onTouchcancel: onTouchEnd
            }, [
              (!__props.menu || __props.title || _ctx.$slots.headerAction)
                ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                    (__props.title)
                      ? (_openBlock(), _createElementBlock("h2", {
                          key: 0,
                          class: "popup__title",
                          innerHTML: __props.title
                        }, null, 8, _hoisted_2))
                      : _createCommentVNode("", true),
                    _renderSlot(_ctx.$slots, "headerAction"),
                    (!__props.menu)
                      ? (_openBlock(), _createElementBlock("button", {
                          key: 1,
                          class: "popup__close",
                          onClick: _cache[1] || (_cache[1] = ($event: any) => (hide()))
                        }, [...(_cache[3] || (_cache[3] = [
                          _createElementVNode("span", { class: "popup__close-icon iconfont iclose-thin" }, null, -1)
                        ]))]))
                      : _createCommentVNode("", true)
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_3, [
                _renderSlot(_ctx.$slots, "default"),
                (_ctx.$slots.buttons)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                      _renderSlot(_ctx.$slots, "buttons")
                    ]))
                  : _createCommentVNode("", true)
              ])
            ], 36)
          ], 2))
        : _createCommentVNode("", true)
    ]),
    _: 3
  }))
}
}

})